import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  Receipt, 
  Clock, 
  ChefHat, 
  Package, 
  Truck, 
  CheckCircle2, 
  XCircle, 
  Printer, 
  RotateCcw, 
  Utensils, 
  CreditCard, 
  ShieldCheck,
  Calendar,
  RefreshCw,
  Sparkles,
  MapPin,
  Phone,
  AlertCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import MoneyUtils from '../../utils/moneyUtils';
import { orderUtils } from '../../utils/orderUtils';
import { useRestaurantContext } from '../../context/RestaurantContext';
import { useCartContext } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { getSelfOrderById, getOrderBillingSummary } from '../../services/api/restApiAdapter';

const AnimatedDots = () => (
  <span className="inline-flex items-center gap-1">
    <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80 animate-pulse" />
    <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80 animate-pulse delay-100" />
    <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80 animate-pulse delay-200" />
  </span>
);

const getStatusDetails = (status) => {
  switch (status?.toLowerCase()) {
    case 'pending':
      return {
        label: 'Waiting for Approval',
        description: 'Restaurant is reviewing your order & stock',
        bg: 'bg-amber-50 text-amber-800 border-amber-200',
        badgeBg: 'bg-amber-500',
        step: 1,
        icon: <Clock size={16} className="text-amber-600" />
      };
    case 'confirmed':
      return {
        label: 'Order Accepted',
        description: 'Restaurant has accepted your order',
        bg: 'bg-blue-50 text-blue-800 border-blue-200',
        badgeBg: 'bg-blue-500',
        step: 2,
        icon: <CheckCircle2 size={16} className="text-blue-600" />
      };
    case 'preparing':
    case 'processing':
      return {
        label: 'Preparing in Kitchen',
        description: 'Chefs are currently preparing your food',
        bg: 'bg-indigo-50 text-indigo-800 border-indigo-200',
        badgeBg: 'bg-indigo-500',
        step: 3,
        icon: <ChefHat size={16} className="text-indigo-600" />
      };
    case 'ready_for_pickup':
      return {
        label: 'Ready for Pickup',
        description: 'Your food is ready at the pickup counter',
        bg: 'bg-emerald-50 text-emerald-800 border-emerald-200',
        badgeBg: 'bg-emerald-500',
        step: 4,
        icon: <Package size={16} className="text-emerald-600" />
      };
    case 'out_for_delivery':
      return {
        label: 'Out for Delivery',
        description: 'Your delivery rider is on the way',
        bg: 'bg-sky-50 text-sky-800 border-sky-200',
        badgeBg: 'bg-sky-500',
        step: 4,
        icon: <Truck size={16} className="text-sky-600" />
      };
    case 'completed':
    case 'delivered':
      return {
        label: 'Order Completed',
        description: 'Order successfully delivered and completed',
        bg: 'bg-green-50 text-green-800 border-green-200',
        badgeBg: 'bg-green-600',
        step: 5,
        icon: <CheckCircle2 size={16} className="text-green-600" />
      };
    case 'cancelled':
    case 'failed':
      return {
        label: 'Order Cancelled',
        description: 'This order was cancelled',
        bg: 'bg-rose-50 text-rose-800 border-rose-200',
        badgeBg: 'bg-rose-600',
        step: 0,
        icon: <XCircle size={16} className="text-rose-600" />
      };
    default:
      return {
        label: status || 'In Progress',
        description: 'Order is being processed',
        bg: 'bg-gray-50 text-gray-800 border-gray-200',
        badgeBg: 'bg-gray-500',
        step: 1,
        icon: <Clock size={16} className="text-gray-600" />
      };
  }
};

const formatServiceType = (type, tableNum, location) => {
  if (tableNum) return orderUtils.formatTableDisplayName(tableNum, true, location);
  const t = (type || '').toLowerCase();
  if (t.includes('dine')) return 'Dine In';
  if (t.includes('takeaway') || t.includes('take_away')) return 'Takeaway';
  if (t.includes('delivery')) return 'Delivery';
  return 'Dine In';
};

const OrderDetailsPage = () => {
  const { restaurantId, orderId } = useParams();
  const navigate = useNavigate();
  const { theme } = useRestaurantContext();
  const { addToCart } = useCartContext();
  const { isAuthLoading } = useAuth();

  const [order, setOrder] = useState(null);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [statusNotification, setStatusNotification] = useState(null);
  const prevStatusRef = useRef(null);

  const accentColor = theme?.secondary || '#000000';
  const textOnAccent = theme?.primary || '#FFFFFF';

  // ── Fetch Order & Billing Summary (Authoritative REST) ─────────────────────────
  const fetchOrderData = useCallback(async (isSilent = false) => {
    if (!restaurantId || !orderId) return;

    if (!isSilent) {
      setLoading(true);
      setError(null);
    } else {
      setIsRefreshing(true);
    }

    try {
      const [orderRes, billingRes] = await Promise.allSettled([
        getSelfOrderById(restaurantId, orderId),
        getOrderBillingSummary(restaurantId, orderId)
      ]);

      if (orderRes.status === 'fulfilled' && orderRes.value?.success && orderRes.value?.data) {
        const orderData = orderRes.value.data;
        setOrder(orderData);
        setError(null);

        // Detect live status change to notify user
        const newStatus = orderData.orderStatus;
        if (prevStatusRef.current && prevStatusRef.current !== newStatus) {
          const newStatusInfo = getStatusDetails(newStatus);
          setStatusNotification({
            label: newStatusInfo.label,
            description: newStatusInfo.description
          });
          if (typeof navigator !== 'undefined' && navigator.vibrate) {
            try {
              navigator.vibrate(200);
            } catch (_) {}
          }
          setTimeout(() => setStatusNotification(null), 4500);
        }
        prevStatusRef.current = newStatus;
      } else if (!isSilent) {
        setError(orderRes.value?.error || 'Order not found');
      }

      if (billingRes.status === 'fulfilled' && billingRes.value?.success && billingRes.value?.data) {
        setBilling(billingRes.value.data);
      }
    } catch (err) {
      if (!isSilent) setError(err.message || 'Failed to load order details');
    } finally {
      if (!isSilent) setLoading(false);
      else setIsRefreshing(false);
    }
  }, [restaurantId, orderId]);

  // Initial Fetch on Mount (waits until Firebase Auth has initialized)
  useEffect(() => {
    if (restaurantId && orderId && !isAuthLoading) {
      fetchOrderData(false);
    }
  }, [restaurantId, orderId, isAuthLoading, fetchOrderData]);

  // ── Real-time Firestore Listener ─────────────────────────────────────────────
  useEffect(() => {
    if (!restaurantId || !orderId) return;

    let unsubscribe = () => {};
    try {
      const db = getFirestore();
      const orderRef = doc(db, 'restaurants', restaurantId, 'clientDisplayOrders', orderId);

      let isInitialSnapshot = true;
      unsubscribe = onSnapshot(orderRef, (docSnapshot) => {
        // Skip the initial snapshot burst since mount fetch handles the authoritative state
        if (isInitialSnapshot) {
          isInitialSnapshot = false;
          return;
        }

        if (docSnapshot.exists()) {
          const snapshotData = docSnapshot.data();
          const realtimeStatus = snapshotData.orderStatus || snapshotData.order_status;

          // 1. Instant zero-latency optimistic status update
          if (realtimeStatus) {
            setOrder((prev) => prev ? { ...prev, orderStatus: realtimeStatus } : prev);
          }

          // 2. Fetch authoritative data (items, billing, settlement) in background
          fetchOrderData(true);
        }
      }, (err) => {
        console.error('Firestore order real-time listener error:', err);
      });
    } catch (err) {
      console.error('Failed to attach Firestore order listener:', err);
    }

    return () => {
      unsubscribe();
    };
  }, [restaurantId, orderId, fetchOrderData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="w-10 h-10 border-3 border-gray-200 border-t-black rounded-full animate-spin mb-3" />
        <p className="text-xs font-semibold text-gray-500">Loading order details...</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4 text-center">
        <div className="w-12 h-12 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center text-rose-600 mb-3">
          <XCircle size={24} />
        </div>
        <h2 className="text-base font-bold text-gray-900 mb-1">Order Details Unavailable</h2>
        <p className="text-xs text-gray-500 max-w-xs mb-4">{error || 'Could not find the requested order.'}</p>
        <button
          onClick={() => navigate(`/restaurant/${restaurantId}/orders`)}
          className="px-4 py-2 rounded-xl text-xs font-bold bg-gray-900 text-white active:scale-95 transition-transform cursor-pointer"
        >
          Return to Orders
        </button>
      </div>
    );
  }

  const orderStatus = order.orderStatus || 'pending';
  const statusDetails = getStatusDetails(orderStatus);
  const isFinalized = ['completed', 'delivered', 'cancelled', 'failed'].includes(orderStatus.toLowerCase());
  const isCancelled = ['cancelled', 'failed'].includes(orderStatus.toLowerCase());
  const cancellationReason = order.cancellationReason || order.cancellation_reason || order.reason;

  const shortCode = orderUtils.getShortCode(order.orderNumber) || order.id?.slice(0, 6);
  const orderDate = order.createdAt || order.created_at ? new Date(order.createdAt || order.created_at) : new Date();
  
  const isSettled = billing ? billing.settlement?.isSettled : (order.paymentStatus === 'completed');
  const settlementInfo = billing?.settlement;

  // Print Receipt Handler
  const handlePrintReceipt = () => {
    window.print();
  };

  // Reorder All Items
  const handleReorder = () => {
    if (order.items && order.items.length > 0) {
      order.items.forEach(item => {
        addToCart({
          id: item.itemId || item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity || 1,
          portion: item.portion,
          addons: item.addons || []
        });
      });
      navigate(`/restaurant/${restaurantId}/checkout`);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] pb-24 text-gray-900">
      {/* ─── Sticky Header ──────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-gray-100 px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => navigate(`/restaurant/${restaurantId}/orders`)}
          className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700 transition-colors active:scale-95 cursor-pointer"
        >
          <ArrowLeft size={18} />
        </button>

        <div className="text-center">
          <h1 className="text-xs font-black uppercase tracking-wider text-gray-400">Order Details</h1>
          <span className="text-sm font-black text-gray-900">#{shortCode}</span>
        </div>

        <div className="flex items-center gap-1.5">
          {/* Live indicator moved here for symmetry */}
          {!isFinalized && (
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" title="Live tracking" />
          )}
          <button
            onClick={() => fetchOrderData(true)}
            disabled={isRefreshing}
            className={`w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700 transition-colors active:scale-95 cursor-pointer ${isRefreshing ? 'opacity-70' : ''}`}
            title="Refresh Order"
          >
            <RefreshCw size={15} className={isRefreshing ? 'animate-spin text-emerald-600' : ''} />
          </button>
          <button
            onClick={handlePrintReceipt}
            className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700 transition-colors active:scale-95 cursor-pointer"
            title="Print Receipt"
          >
            <Printer size={16} />
          </button>
        </div>
      </header>



      <main className="max-w-xl mx-auto p-3.5 space-y-3.5">
        {/* ─── Hero Status Banner Card ─────────────────────────────────────────── */}
        <motion.section
          key={orderStatus}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-4 border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.04)]"
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${statusDetails.bg}`}>
                {statusDetails.icon}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <h2 className="text-sm font-black text-gray-900 truncate">{statusDetails.label}</h2>
                  {!isFinalized && <AnimatedDots />}
                </div>
                <p className="text-[11px] text-gray-500 font-medium truncate">{statusDetails.description}</p>
              </div>
            </div>

            <div 
              className="px-2.5 py-1 rounded-lg text-xs font-black tracking-wider whitespace-nowrap shrink-0"
              style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
            >
              #{shortCode}
            </div>
          </div>

          {/* Visual Order Progress Bar / Inline Status Update (swaps on status change) */}
          {!isCancelled && (
            <div className="pt-2 border-t border-gray-100 overflow-hidden">
              <AnimatePresence mode="wait">
                {statusNotification ? (
                  // ── Status Change Notification (replaces stepper temporarily) ──
                  <motion.div
                    key="notification"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="flex items-center gap-2.5 py-1.5 px-1"
                  >
                    <div className="w-7 h-7 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
                      <Sparkles size={13} className="text-emerald-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-black text-gray-900 leading-tight">{statusNotification.label}</p>
                      <p className="text-[10px] text-gray-500 font-medium truncate">{statusNotification.description}</p>
                    </div>
                  </motion.div>
                ) : (
                  // ── Normal 5-Step Progress Bar ──────────────────────────────────
                  <motion.div
                    key="stepper"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="grid grid-cols-5 gap-1 text-center py-1.5"
                  >
                    {['Sent', 'Accepted', 'Preparing', 'Ready', 'Done'].map((stepName, i) => {
                      const stepIndex = i + 1;
                      const isDone = statusDetails.step >= stepIndex;
                      const isCurrent = statusDetails.step === stepIndex;
                      return (
                        <div key={stepName} className="space-y-1">
                          <div className="h-1.5 rounded-full overflow-hidden bg-gray-100">
                            <div
                              className={`h-full transition-all duration-500 ${
                                isDone ? 'bg-emerald-500' : 'bg-transparent'
                              }`}
                            />
                          </div>
                          <span className={`text-[9px] sm:text-[10px] block font-bold truncate ${
                            isCurrent ? 'text-gray-900 font-black' : isDone ? 'text-emerald-700' : 'text-gray-400'
                          }`}>
                            {stepName}
                          </span>
                        </div>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Reason for Cancellation (Shown only when order is cancelled/failed AND a reason exists) */}
          {isCancelled && cancellationReason && cancellationReason.trim().length > 0 && (
            <div className="pt-2.5 border-t border-gray-100">
              <div className="bg-rose-50/80 border border-rose-200/80 rounded-xl p-3 flex items-start gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center shrink-0 mt-0.5">
                  <AlertCircle size={14} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] font-black uppercase tracking-wider text-rose-700">
                      Reason for Cancellation
                    </span>
                    <span className="text-[9px] font-bold text-rose-600 bg-rose-100/80 px-1.5 py-0.5 rounded">
                      Declined
                    </span>
                  </div>
                  <p className="text-xs font-semibold text-rose-950 mt-1 leading-relaxed break-words">
                    {cancellationReason.trim()}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Meta Timestamps */}
          <div className="mt-3 pt-2.5 border-t border-gray-100 flex items-center justify-between text-[10px] text-gray-500 font-semibold">
            <span className="flex items-center gap-1">
              <Calendar size={11} className="text-gray-400" />
              {format(orderDate, 'dd MMMM yyyy, hh:mm a')}
            </span>
            <span className="flex items-center gap-1 font-bold text-gray-700">
              <Utensils size={11} className="text-gray-400" />
              {formatServiceType(order.orderType, order.tableNumber, order.tableLocation || order.section)}
            </span>
          </div>
        </motion.section>

        {/* ─── Delivery & Contact Details Card (if Delivery order) ────────────── */}
        {(order.orderType === 'delivery' || order.deliveryAddress) && (
          <motion.section
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.04 }}
            className="bg-white rounded-2xl p-4 border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.04)] space-y-3"
          >
            <div className="flex items-center justify-between pb-2.5 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                  <Truck size={16} />
                </div>
                <div>
                  <h3 className="text-xs font-black uppercase tracking-wider text-gray-900">
                    Delivery Address
                  </h3>
                  <span className="text-[10px] text-gray-400 font-medium">Home delivery order</span>
                </div>
              </div>

              {order.deliveryAddress?.label && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 text-[10px] font-black uppercase tracking-wider border border-purple-200/60">
                  {order.deliveryAddress.label}
                </span>
              )}
            </div>

            {/* Recipient info & Phone */}
            <div className="flex items-center justify-between bg-gray-50/80 rounded-xl p-3 text-xs">
              <div className="min-w-0 pr-2">
                <p className="font-black text-gray-900 text-xs truncate">
                  {order.customerName || order.recipientName || 'Customer'}
                </p>
                <p className="text-[11px] text-gray-500 font-medium mt-0.5">
                  {order.phone || order.customerPhone || 'No phone provided'}
                </p>
              </div>

              {order.phone && (
                <a
                  href={`tel:${order.phone}`}
                  className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700 hover:text-emerald-700 hover:border-emerald-300 text-xs font-bold flex items-center gap-1.5 shadow-2xs transition-all active:scale-95 cursor-pointer"
                >
                  <Phone size={12} className="text-emerald-600" />
                  <span>Call</span>
                </a>
              )}
            </div>

            {/* Full Address */}
            {order.deliveryAddress && (
              <div className="space-y-1 text-xs px-1">
                <div className="flex items-start gap-2 text-gray-700 font-medium leading-relaxed">
                  <MapPin size={14} className="text-gray-400 shrink-0 mt-0.5" />
                  <p>
                    {order.deliveryAddress.address || [
                      order.deliveryAddress.addressLine1,
                      order.deliveryAddress.addressLine2,
                      order.deliveryAddress.landmark ? `(Landmark: ${order.deliveryAddress.landmark})` : null,
                      order.deliveryAddress.city,
                      order.deliveryAddress.postalCode,
                    ].filter(Boolean).join(', ')}
                  </p>
                </div>
              </div>
            )}

            {/* Delivery Boy Details if assigned */}
            {order.deliveryBoyDetails && order.deliveryBoyDetails.name && (
              <div className="mt-2 pt-2.5 border-t border-gray-100 flex items-center justify-between text-xs">
                <div>
                  <span className="text-[10px] uppercase font-bold text-gray-400 block">Delivery Partner</span>
                  <p className="font-bold text-gray-900">{order.deliveryBoyDetails.name}</p>
                </div>
                {order.deliveryBoyDetails.phone && (
                  <a
                    href={`tel:${order.deliveryBoyDetails.phone}`}
                    className="px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-bold flex items-center gap-1"
                  >
                    <Phone size={11} />
                    <span>{order.deliveryBoyDetails.phone}</span>
                  </a>
                )}
              </div>
            )}
          </motion.section>
        )}

        {/* ─── Itemized Bill Breakdown Card ────────────────────────────────────── */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-2xl p-4 border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.04)]"
        >
          <div className="flex items-center justify-between pb-3 border-b border-gray-100 gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <Receipt size={15} className="text-gray-400 shrink-0" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 whitespace-nowrap">
                Items ({(order.items || []).length})
              </h3>
            </div>
            {billing?.bill?.billNumber && (
              <span className="text-[10px] font-black px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 whitespace-nowrap shrink-0">
                Bill #{orderUtils.getShortCode(billing.bill.billNumber) || billing.bill.billNumber}
              </span>
            )}
          </div>

          <div className="divide-y divide-gray-50 py-1">
            {(order.items || []).map((item, idx) => {
              const addons = item.addons || item.customizations?.addons || [];
              const combos = item.comboComponents || [];
              const quantity = item.quantity || 1;
              const unitPrice = item.price || item.unitPrice || 0;
              const lineTotal = unitPrice * quantity;
              const portionName = item.portionSize || item.portion?.name;

              return (
                <div key={idx} className="py-2.5 first:pt-2 last:pb-2 flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span 
                        className="w-5 h-5 rounded-md text-[10px] font-black flex items-center justify-center shrink-0"
                        style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
                      >
                        {quantity}x
                      </span>
                      <span className="text-xs font-bold text-gray-900">
                        {orderUtils.capitalize(item.name)}
                      </span>
                    </div>

                    {portionName ? (
                      <p className="text-[10px] text-gray-500 ml-7">
                        Portion: {orderUtils.capitalize(portionName)}
                      </p>
                    ) : null}

                    {addons.length > 0 && (
                      <div className="ml-7 space-y-0.5 mt-0.5">
                        {addons.map((a, aIdx) => (
                          <div key={aIdx} className="text-[10px] text-gray-400 flex items-center justify-between">
                            <span>+ {orderUtils.capitalize(a.name)}</span>
                            {a.price > 0 && <span>+{MoneyUtils.format(a.price * (a.quantity || 1))}</span>}
                          </div>
                        ))}
                      </div>
                    )}

                    {combos.length > 0 && (
                      <div className="ml-7 space-y-0.5 mt-0.5 text-[10px] text-gray-400">
                        {combos.map((c, cIdx) => (
                          <div key={cIdx}>• {orderUtils.capitalize(c.name)}: {orderUtils.capitalize(c.selectedOptionName || 'Standard')}</div>
                        ))}
                      </div>
                    )}

                    {item.cookingInstruction && (
                      <p className="text-[10px] text-amber-700 italic ml-7 mt-0.5">
                        "{orderUtils.capitalize(item.cookingInstruction)}"
                      </p>
                    )}
                  </div>

                  <span className="text-xs font-bold text-gray-900 shrink-0">
                    {MoneyUtils.format(lineTotal)}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Detailed Financial & Tax Summary */}
          <div className="border-t border-gray-100 pt-3 space-y-1.5 text-[11px]">
            <div className="flex justify-between text-gray-500">
              <span>Items Subtotal</span>
              <span className="font-semibold text-gray-800">
                {MoneyUtils.format(billing?.bill?.subtotal || order.itemTotal || 0)}
              </span>
            </div>

            {/* Taxes breakdown */}
            {(billing?.bill?.taxSummary || []).length > 0 ? (
              billing.bill.taxSummary.map((tax, tIdx) => (
                <div key={tIdx} className="flex justify-between text-gray-500">
                  <span>{tax.taxName?.toUpperCase()} ({tax.rate}%)</span>
                  <span className="font-semibold text-gray-800">{MoneyUtils.format(tax.amount)}</span>
                </div>
              ))
            ) : (order.taxesAndCharges || order.taxAmount) > 0 ? (
              <div className="flex justify-between text-gray-500">
                <span>Taxes & Charges</span>
                <span className="font-semibold text-gray-800">
                  {MoneyUtils.format(order.taxesAndCharges || order.taxAmount)}
                </span>
              </div>
            ) : null}

            {order.deliveryFee > 0 && (
              <div className="flex justify-between text-gray-500">
                <span>Delivery Fee</span>
                <span className="font-semibold text-gray-800">{MoneyUtils.format(order.deliveryFee)}</span>
              </div>
            )}

            {order.tipAmount > 0 && (
              <div className="flex justify-between text-gray-500">
                <span>Tip</span>
                <span className="font-semibold text-gray-800">{MoneyUtils.format(order.tipAmount)}</span>
              </div>
            )}

            {(billing?.bill?.totalDiscount || order.discountAmount) > 0 && (
              <div className="flex justify-between text-emerald-600 font-bold">
                <span>Discount</span>
                <span>-{MoneyUtils.format(billing?.bill?.totalDiscount || order.discountAmount)}</span>
              </div>
            )}

            {/* Credit Note / Refund deductions */}
            {billing?.refundSummary?.hasRefunds && (
              <div className="flex justify-between text-rose-600 font-bold">
                <span>Credit Note / Refund</span>
                <span>-{MoneyUtils.format(billing.refundSummary.totalCreditedAmount || billing.refundSummary.totalRefundedAmount)}</span>
              </div>
            )}

            {/* Final Grand Total */}
            <div className="border-t border-gray-200 pt-2 flex justify-between items-center text-xs">
              <span className="font-bold text-gray-900">Total Net Amount</span>
              <span className="font-black text-sm text-gray-900">
                {MoneyUtils.format(billing?.bill?.netPayableAmount || billing?.settlement?.grandTotal || order.finalAmount || 0)}
              </span>
            </div>
          </div>
        </motion.section>

        {/* ─── Payment & Settlement Ledger Card ────────────────────────────────── */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl p-4 border border-gray-100 shadow-[0_2px_12px_rgba(0,0,0,0.04)] space-y-3"
        >
          <div className="flex items-center justify-between pb-2 border-b border-gray-100 gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <CreditCard size={15} className="text-gray-400 shrink-0" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 whitespace-nowrap">
                Payment
              </h3>
            </div>

            <span className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider whitespace-nowrap shrink-0 ${
              isSettled ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'
            }`}>
              {isSettled ? 'PAID' : 'NOT PAID'}
            </span>
          </div>

          <div className="space-y-2 text-xs">
            <div className="flex justify-between items-center text-gray-600">
              <span>{isSettled ? 'Payment Method' : 'Payment Status'}</span>
              <span className={`font-bold ${isSettled ? 'text-gray-900' : 'text-amber-700'}`}>
                {isSettled 
                  ? (settlementInfo?.displayPaymentLabel || 'Cash') 
                  : 'Not Paid'}
              </span>
            </div>

            <div className="flex justify-between items-center text-gray-600">
              <span>Settled Amount</span>
              <span className="font-bold text-gray-900">
                {MoneyUtils.format(settlementInfo?.paidAmount || 0)}
              </span>
            </div>

            {(!isSettled || (settlementInfo?.balanceAmount > 0)) && (
              <div className="flex justify-between items-center text-amber-700 font-bold">
                <span>Balance Due</span>
                <span>{MoneyUtils.format(settlementInfo?.balanceAmount ?? order.finalAmount ?? 0)}</span>
              </div>
            )}

            {/* Individual Captured Payments */}
            {(billing?.payments || []).length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-50 space-y-1">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">
                  Transaction Records
                </span>
                {billing.payments.map((pmt, pIdx) => (
                  <div key={pIdx} className="bg-gray-50/80 rounded-xl p-2 flex items-center justify-between text-[11px]">
                    <div>
                      <span className="font-bold text-gray-800 uppercase">{pmt.method}</span>
                      <span className="text-[10px] text-gray-400 block">
                        {pmt.createdAt ? format(new Date(pmt.createdAt), 'hh:mm a') : 'Completed'}
                      </span>
                    </div>
                    <span className="font-black text-gray-900">{MoneyUtils.format(pmt.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.section>

        {/* ─── Credit Notes / Refunds Section (If Present) ────────────────────── */}
        {(billing?.creditNotes || []).length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-rose-50/50 rounded-2xl p-4 border border-rose-100 shadow-[0_2px_12px_rgba(0,0,0,0.02)] space-y-2"
          >
            <div className="flex items-center gap-2 text-rose-700">
              <ShieldCheck size={16} />
              <h3 className="text-xs font-bold uppercase tracking-wider">Credit Note / Refunds Issued</h3>
            </div>

            <div className="space-y-1.5">
              {billing.creditNotes.map((cn, cnIdx) => (
                <div key={cnIdx} className="bg-white rounded-xl p-2.5 border border-rose-100 flex items-start justify-between text-xs">
                  <div>
                    <span className="font-bold text-gray-900 block">{cn.creditNoteNumber}</span>
                    <span className="text-[10px] text-gray-500">Reason: {cn.reason || 'Order adjustment'}</span>
                  </div>
                  <span className="font-black text-rose-600">-{MoneyUtils.format(cn.amount)}</span>
                </div>
              ))}
            </div>
          </motion.section>
        )}

        {/* ─── Footer Action Buttons ─────────────────────────────────────────── */}
        <div className="pt-2 flex flex-col gap-2">
          <button
            onClick={() => navigate(`/restaurant/${restaurantId}/orders`)}
            className="w-full py-3 rounded-xl font-bold text-xs bg-white border border-gray-200 text-gray-700 flex items-center justify-center gap-2 transition-all active:scale-98 cursor-pointer shadow-2xs hover:bg-gray-50"
          >
            <ArrowLeft size={15} />
            <span>Back to All Orders</span>
          </button>
        </div>
      </main>
    </div>
  );
};

export default OrderDetailsPage;
